import { useCallback, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PERSONAS } from "@/data/content";
import { MOSAIC_GROUPS } from "@/data/mosaic";
import { PersonaArt } from "@/components/silver/story/PersonaArt";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const SPRING = { type: "spring", stiffness: 130, damping: 22, mass: 0.9 };

function PersonaCard({ persona, index, offset, cardW, onSelect }) {
  const abs = Math.abs(offset);
  const active = offset === 0;
  const profiles = MOSAIC_GROUPS.filter(persona.match);
  return (
    <motion.article
      className={`glass absolute left-1/2 top-0 flex cursor-pointer select-none flex-col overflow-hidden p-7 ${active ? "glass-active" : ""}`}
      style={{ width: cardW, marginLeft: -cardW / 2, zIndex: 10 - abs, pointerEvents: abs > 2 ? "none" : "auto" }}
      animate={{
        x: offset * cardW * 0.84,
        scale: active ? 1 : 0.82,
        opacity: abs > 2 ? 0 : active ? 1 : 0.5,
        filter: active ? "brightness(1) saturate(1)" : "brightness(0.65) saturate(0.7)",
      }}
      transition={SPRING}
      onClick={() => onSelect(index)}
      aria-hidden={!active}
      data-testid={`persona-card-${persona.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="eyebrow-muted">Audience grouping</span>
        <span className="font-ui text-[11px] font-semibold tracking-[0.12em] text-white/40">0{index + 1} / 0{PERSONAS.length}</span>
      </div>
      <div className="mt-5 h-[124px] overflow-hidden rounded-2xl border border-white/[0.06] bg-black/10">
        <PersonaArt motif={persona.motif} />
      </div>
      <h3 className="mt-6 text-[1.75rem] font-extrabold leading-tight tracking-[-0.035em]">{persona.name}</h3>
      <ul className="mt-4 space-y-2.5">
        {persona.lines.map((l) => (
          <li key={l} className="flex items-center gap-3 text-[15px] text-white/75">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#96C11F]" /> {l}
          </li>
        ))}
      </ul>
      <div className="mt-auto pt-6">
        <div className="hairline mb-4" />
        <p className="font-ui text-[11px] uppercase tracking-[0.12em] text-white/45">Built from {profiles.length} audience profiles</p>
        <p className="mt-1.5 truncate font-ui text-[11px] tracking-[0.08em] text-white/30">{profiles.map((g) => g.code).join(" · ")}</p>
      </div>
    </motion.article>
  );
}

export default function PersonaCarousel() {
  const [index, setIndex] = useState(0);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const cardW = isMobile ? 290 : 390;
  const go = useCallback((d) => setIndex((i) => Math.max(0, Math.min(PERSONAS.length - 1, i + d))), []);

  return (
    <section id="audience" className="relative overflow-hidden py-24 lg:py-36" data-testid="persona-carousel-section">
      <div className="mx-auto flex max-w-7xl flex-col gap-8 px-5 sm:px-8 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <p className="eyebrow">The audience</p>
          <h2 className="mt-6 text-4xl font-extrabold leading-[1.02] tracking-[-0.045em] sm:text-5xl lg:text-[3.75rem]">Not simply older homeowners.</h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/65 sm:text-[17px]">
            Distinct life stages, property circumstances and potential next moves. Five marketing groupings built from the underlying audience profiles — household numbers appear once you choose your patch.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-ghost h-12 w-12 !p-0" onClick={() => go(-1)} disabled={index === 0} aria-label="Previous audience" data-testid="persona-prev-btn"><ArrowLeft className="h-4 w-4" /></button>
          <button className="btn-ghost h-12 w-12 !p-0" onClick={() => go(1)} disabled={index === PERSONAS.length - 1} aria-label="Next audience" data-testid="persona-next-btn"><ArrowRight className="h-4 w-4" /></button>
        </div>
      </div>

      <motion.div
        className="relative mt-16 h-[540px] cursor-grab touch-pan-y active:cursor-grabbing"
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.16}
        onDragEnd={(_, info) => {
          if (info.offset.x < -50 || info.velocity.x < -450) go(1);
          else if (info.offset.x > 50 || info.velocity.x > 450) go(-1);
        }}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label={`Audience groupings, showing ${PERSONAS[index].name}`}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") go(1);
          if (e.key === "ArrowLeft") go(-1);
        }}
        data-testid="persona-carousel"
      >
        {PERSONAS.map((p, i) => (
          <PersonaCard key={p.id} persona={p} index={i} offset={i - index} cardW={cardW} onSelect={setIndex} />
        ))}
      </motion.div>

      <div className="mt-8 flex justify-center gap-2" role="tablist" aria-label="Choose audience grouping">
        {PERSONAS.map((p, i) => (
          <button key={p.id} role="tab" aria-selected={i === index} aria-label={p.name} onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-10 bg-[#C6EE5A]" : "w-4 bg-white/20 hover:bg-white/40"}`} data-testid={`persona-dot-${p.id}`} />
        ))}
      </div>
    </section>
  );
}
